import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { useBuyerList } from './useBuyerList';
import { BuyerCard } from './BuyerCard';
import styled from '@emotion/styled';
import IconArrowDown from 'ui/images/form/icon_arrow_down_white.svg';
import IconSearch from 'ui/images/form/search.svg';
import { AboutService } from 'ui/components/company/features/AboutService';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';
import { Pagination } from 'ui/components/agent/features/Pagination/Pagination';
import { mqUser, mqMin } from 'utils/responsive';

const Wrapper = styled.div`
    padding: 1.125rem 3.125rem;
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
    gap: 1.125rem 1.5625rem;
    align-content: flex-start;
    align-items: flex-start;

    margin: 0 auto;
    background-color: #f8f9fd;
    min-height: 100svh;

    @keyframes fadeIn {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
    animation: ${({ loading }: { loading: boolean }) => (loading ? 'none' : 'fadeIn 0.2s ease-in-out')};

    ${mqUser('SP')} {
        padding: 1.125rem 0.9375rem;
    }
`;

const SearchWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    width: 100%;
    margin-bottom: 1.25rem;
`;

const SearchFormWrapper = styled.div<{ isOpen: boolean }>`
    width: 100%;
    display: flex;
    background-color: var(--main-color-blue);
    margin: 0 auto;
    border-radius: 0.5rem;
    color: var(--white);
    padding: 0.9375rem 0;
    font-weight: var(--font-bold);
    cursor: pointer;

    img:nth-of-type(1) {
        width: 1.125rem;
        cursor: pointer;
        margin-right: 0.3125rem;
        margin-left: 1.25rem;
    }
    img:nth-of-type(2) {
        margin-left: auto;
        margin-right: 1.5625rem;
        width: 1.25rem;
        cursor: pointer;
        transition: transform 0.3s ease;
        transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
    }
`;

const SearchArea = styled.div<{ isOpen: boolean }>`
    width: 100%;
    background-color: var(--white);
    padding: 1.25rem;
    border-radius: 0.5rem;
    margin-bottom: 0.625rem;
    gap: 1rem;
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    flex-direction: column;
`;

const OptionItems = styled.div`
    display: flex;
    gap: 0.625rem 1rem;
    margin-top: 0.625rem;
    flex-wrap: wrap;
`;

const OptionItem = styled.label`
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    white-space: nowrap;
    font-size: var(--font-medium);

    input {
        margin-right: 0.3125rem;
        appearance: auto !important;
    }
`;

const Item = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.3125rem;
    margin: 0 1.25rem;
    flex: 1;

    ${mqMin('NARROW')} {
        flex: 1;
        min-width: 0;
        margin: 0;
    }
`;

const Title = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);

    position: relative;
    padding-left: 0.625rem;
    &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        width: 0.25rem;
        height: 100%;
        background-color: var(--main-color-blue);
        transform: translateY(-50%);
        border-radius: 0.125rem;
    }
`;

const SearchButton = styled.button`
    width: 18.75rem;
    background-color: var(--primary);
    color: var(--white);
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    padding: 0.625rem;
    border-radius: 1.5625rem;
    cursor: pointer;
    margin: 0 auto;
    border: none;
    outline: none;
`;

const BuyerCardContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 20px 25px;
    margin: 0 auto;
    width: 100%;
`;

const tabs = ['オファーについて', 'プレミアムオファー'];

const descriptions: { [key: string]: string[] } = {
    オファーについて: [
        '気になる案件にオファーを送りましょう。買い手企業がオファーを承諾すると、企業名などの詳細情報と個別のチャットが開通されます。',
        '買い手企業がオファーを承諾し、情報開示＆チャットの開通時に1件あたり5万円が課金（未承諾なら無料）されます。初期費用・月額費用は一切なく、完全成果課金型のリード提供を行います。',
    ],
    プレミアムオファー: [
        'プレミアムオファーを行うと、以下特典があります。',
        '１．買い手企業がMAnyにログインした際にポップアップ通知が表示されます。',
        '２．マッチング後も、買い手企業の受信BOX内で2週間上位に表示され、他のDMより発見性・開封率が高くなります。',
        'プレミアムオファーの送信には1件20,000円（税抜き）が課金され、この金額はオファーが未成立の場合でも返金は行われません。',
    ],
};

export const BuyerList: React.FC = () => {
    const {
        loading,
        buyers,
        areaChoices,
        industryChoices,
        setValue,
        submit,
        control,
        currentPage,
        hasNextPage,
        handlePreviousPage,
        handleNextPage,
        handlePageChange,
    } = useBuyerList();
    const [isOpen, setIsOpen] = useState(false);

    const toggleSearchArea = () => {
        setIsOpen(!isOpen);
    };

    return (
        <Wrapper loading={loading}>
            <PageTitle title="買い手企業一覧" />
            <AboutService tabs={tabs} descriptions={descriptions} />
            {!loading && (
                <>
                    <SearchWrapper>
                        <SearchFormWrapper onClick={toggleSearchArea} isOpen={isOpen}>
                            <img src={IconSearch} alt="search" />
                            絞り込み検索
                            <img src={IconArrowDown} alt="arrow-down" />
                        </SearchFormWrapper>
                        <SearchArea isOpen={isOpen}>
                            {/* 数値範囲の検索がユーザー目線でわかりにくくなる為無効化している */}
                            {/* <RangeItemsContainer>
                                <Item>
                                    <Title>買収希望案件の従業員数</Title>
                                    <OptionItems>
                                        <RangeContainer>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_employee_size_lower"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_employee_size_lower',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {employeeLowerChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                            <TildeText>〜</TildeText>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_employee_size_upper"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_employee_size_upper',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {employeeUpperChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                        </RangeContainer>
                                    </OptionItems>
                                </Item>
                                <Item>
                                    <Title>買収希望案件の売上規模範囲</Title>
                                    <OptionItems>
                                        <RangeContainer>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_revenue_scale_lower"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_revenue_scale_lower',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {revenueScaleLowerChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                            <TildeText>〜</TildeText>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_revenue_scale_upper"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_revenue_scale_upper',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {revenueScaleUpperChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                        </RangeContainer>
                                    </OptionItems>
                                </Item>
                                <Item>
                                    <Title>買収希望案件の利益水準</Title>
                                    <OptionItems>
                                        <RangeContainer>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_profit_level_lower"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_profit_level_lower',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {profitLevelLowerChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                            <TildeText>〜</TildeText>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_profit_level_upper"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_profit_level_upper',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {profitLevelUpperChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                        </RangeContainer>
                                    </OptionItems>
                                </Item>
                                <Item>
                                    <Title>買収希望案件の純資産水準</Title>
                                    <OptionItems>
                                        <RangeContainer>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_net_assets_level_lower"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_net_assets_level_lower',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {netAssetsLevelLowerChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                            <TildeText>〜</TildeText>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_net_assets_level_upper"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_net_assets_level_upper',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {netAssetsLevelUpperChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                        </RangeContainer>
                                    </OptionItems>
                                </Item>
                                <Item>
                                    <Title>買収希望金額</Title>
                                    <OptionItems>
                                        <RangeContainer>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_purchase_amount_lower"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_purchase_amount_lower',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {purchaseAmountLowerChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                            <TildeText>〜</TildeText>
                                            <SelectContainer>
                                                <Controller
                                                    control={control}
                                                    name="target_company_purchase_amount_upper"
                                                    render={({ field }) => (
                                                        <Select
                                                            value={field.value || ''}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setValue(
                                                                    'target_company_purchase_amount_upper',
                                                                    e.target.value,
                                                                );
                                                            }}
                                                        >
                                                            <option value="">選択してください</option>
                                                            {purchaseAmountUpperChoices.map(([value, label]) => (
                                                                <option key={value} value={value}>
                                                                    {label}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    )}
                                                />
                                            </SelectContainer>
                                        </RangeContainer>
                                    </OptionItems>
                                </Item>
                            </RangeItemsContainer> */}
                            <Item>
                                <Title>買収希望案件の業種</Title>
                                <OptionItems>
                                    <Controller
                                        control={control}
                                        name="target_company_industries"
                                        render={({ field }) => (
                                            <>
                                                {industryChoices.map(([value, label]) => (
                                                    <OptionItem key={value}>
                                                        <input
                                                            type="checkbox"
                                                            value={value}
                                                            checked={
                                                                Array.isArray(field.value)
                                                                    ? field.value.includes(value)
                                                                    : false
                                                            }
                                                            onChange={(e) => {
                                                                const newValue = e.target.value;
                                                                const updatedValue = Array.isArray(field.value)
                                                                    ? field.value.includes(newValue)
                                                                        ? field.value.filter((v) => v !== newValue)
                                                                        : [...field.value, newValue]
                                                                    : [newValue];
                                                                field.onChange(updatedValue);
                                                                setValue('target_company_industries', updatedValue);
                                                            }}
                                                        />
                                                        {label}
                                                    </OptionItem>
                                                ))}
                                            </>
                                        )}
                                    />
                                </OptionItems>
                            </Item>
                            <Item>
                                <Title>買収希望案件エリア</Title>
                                <OptionItems>
                                    <Controller
                                        control={control}
                                        name="area"
                                        render={({ field }) => (
                                            <>
                                                {areaChoices.map(([value, label]) => (
                                                    <OptionItem key={value}>
                                                        <input
                                                            type="checkbox"
                                                            value={value}
                                                            checked={
                                                                Array.isArray(field.value)
                                                                    ? field.value.includes(value)
                                                                    : false
                                                            }
                                                            onChange={(e) => {
                                                                const newValue = e.target.value;
                                                                const updatedValue = Array.isArray(field.value)
                                                                    ? field.value.includes(newValue)
                                                                        ? field.value.filter((v) => v !== newValue)
                                                                        : [...field.value, newValue]
                                                                    : [newValue];
                                                                field.onChange(updatedValue);
                                                                setValue('area', updatedValue);
                                                            }}
                                                        />
                                                        {label}
                                                    </OptionItem>
                                                ))}
                                            </>
                                        )}
                                    />
                                </OptionItems>
                            </Item>

                            <SearchButton onClick={submit}>検索</SearchButton>
                        </SearchArea>
                    </SearchWrapper>
                    <BuyerCardContainer>
                        {buyers.map((buyer) => (
                            <BuyerCard key={buyer.id} buyer={buyer} />
                        ))}
                    </BuyerCardContainer>
                    {buyers.length > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            hasNextPage={hasNextPage}
                            onPreviousPage={handlePreviousPage}
                            onNextPage={handleNextPage}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            )}
        </Wrapper>
    );
};
